import Vue from 'vue';
import Vuex from 'vuex';
import DashboardApi from '../api/dashboard';
import QuestionnaireApi from '../api/questionnaire';
import checkup from './checkup';
import test from './test';
import clinic from './clinic';

Vue.use(Vuex);

const env = process.env;
const dashboardApi = new DashboardApi(
  env.VUE_APP_API_DASHBOARD_URL,
  env.VUE_APP_API_DASHBOARD_TIMEOUT
);
const questionnaireApi = new QuestionnaireApi(
  env.VUE_APP_API_QUESTIONNAIRE_URL,
  env.VUE_APP_API_QUESTIONNAIRE_TIMEOUT
);

const state = {
  view: '',
  error: '',
  clientId: null,
  token: localStorage.token || '',
  catalogs: [],
  favorites: [],
  formats: [],
  ingredients: [],
  methods: [],
  times: [],
  drugs: [],
  clients: [],
  drugStats: {},
  drugPrescriptions: {},
  questionnaires: [],
  answers: {},
};

const getters = {
  isAuthorized: state => {
    if (typeof state.token !== 'string') {
      return false;
    }

    return state.token.trim() !== '' && state.token.trim() !== 'undefined';
  },

  clientDrugStats: state => clientId => {
    if (!state.drugStats[clientId]) {
      return {};
    }

    return state.drugStats[clientId];
  },

  clientDrugPrescriptions: state => clientId => {
    if (!state.drugPrescriptions[clientId]) {
      return [];
    }

    return state.drugPrescriptions[clientId];
  },

  clientAnswers: state => clientId => {
    if (!state.answers[clientId] || state.answers[clientId].length === 0) {
      return [];
    }

    return state.answers[clientId];
  },
};

const mutations = {
  SET_ERROR(state, error) {
    state.error = error;
  },

  SET_VIEW(state, view) {
    state.view = view;
  },

  SET_CLIENT_ID(state, clientId) {
    state.clientId = clientId;
  },

  SET_AUTH_TOKEN(state, token) {
    state.token = token;
    localStorage.token = token;
  },

  SET_CLIENTS: (state, payload) => {
    for (const client of payload) {
      client.flags = {};
      client.name = `${client.lastname} ${client.firstname}`;
      client.image.url = env.VUE_APP_API_DASHBOARD_URL + client.image.url;

      try {
        const extra = JSON.parse(client.features);

        if (typeof extra.flags === 'object') {
          for (const flag of Object.keys(extra.flags)) {
            client.flags[flag] = extra.flags[flag];
          }
        }
      } catch (e) {
        // do-noting
      }
    }

    state.clients = payload;
  },

  SET_CATALOGS: (state, payload) => {
    state.catalogs = payload;
  },

  SET_DRUG_STATS: (state, payload) => {
    payload.forEach(item => {
      state.drugStats[item.patientId] = item;
    });

    state.drugStats = Object.assign({}, state.drugStats);
  },

  SET_DRUG_PRESCRIPTIONS: (state, payload) => {
    state.drugPrescriptions[payload.clientId] = payload.items;
    state.drugPrescriptions = Object.assign({}, state.drugPrescriptions);

    state.drugPrescriptions[payload.clientId].forEach(prescription => {
      prescription.methods.sort((a, b) => a.sort - b.sort);
      prescription.times.sort((a, b) => a.sort - b.sort);
    });
  },

  SET_FAVORITES_DRUG_PRESCRIPTIONS: (state, payload) => {
    state.favorites = payload;

    state.favorites.forEach(prescription => {
      prescription.methods.sort((a, b) => a.sort - b. sort);
      prescription.times.sort((a, b) => a.sort - b. sort);
    });
  },

  SET_FORMATS: (state, payload) => {
    state.formats = payload;
  },

  SET_INGREDIENTS: (state, payload) => {
    state.ingredients = payload;
  },

  SET_METHODS: (state, payload) => {
    state.methods = payload;

    state.methods.sort((a, b) => a.sort - b. sort);
  },

  SET_TIMES: (state, payload) => {
    state.times = payload;

    state.times.sort((a, b) => a.sort - b. sort);
  },

  SET_DRUGS: (state, payload) => {
    state.drugs = payload;
  },

  SET_QUESTIONNAIRES: (state, payload) => {
    state.questionnaires = payload;
  },

  SET_QUESTIONNAIRE_ANSWERS: (state, payload) => {
    state.answers[payload.clientId] = payload.items;

    state.answers = Object.assign({}, state.answers);
  },
};

const actions = {
  ERROR: async (context, error) => {
    const message = error && error.message ? error.message : 'Something went wrong';

    if (error.response && error.response.status && [401, 403].includes(error.response.status)) {
      context.commit('SET_AUTH_TOKEN', '');
    } else {
      context.commit('SET_ERROR', message);
    }
  },

  CHANGE_VIEW: async (context, payload) => {
    context.commit('SET_VIEW', payload);
  },

  CHOOSE_CLIENT: async (context, payload) => {
    context.commit('SET_CLIENT_ID', payload);
  },

  LOG_IN: async (context, payload) => {
    const {login, password} = payload;
    const data = await dashboardApi.logIn(login, password);

    context.commit('SET_AUTH_TOKEN', data.token);
  },

  LOG_OUT: async (context) => {
    const token = context.state.token;

    context.commit('SET_AUTH_TOKEN', '');

    await dashboardApi.logOut(token);
  },

  LOAD_CLIENTS: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getClients(token);

    context.commit('SET_CLIENTS', data.items);
  },

  LOAD_CATALOGS: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getCatalogs(token);

    context.commit('SET_CATALOGS', data.items);
  },

  LOAD_FORMATS: async (context,) => {
    const token = context.state.token;
    const data = await dashboardApi.getFormats(token);

    context.commit('SET_FORMATS', data.items);
  },

  LOAD_INGREDIENTS: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getIngredients(token);

    context.commit('SET_INGREDIENTS', data.items);
  },

  LOAD_METHODS: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getMethods(token);

    context.commit('SET_METHODS', data.items);
  },

  LOAD_TIMES: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getTimes(token);

    context.commit('SET_TIMES', data.items);
  },

  LOAD_DRUGS: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getDrugs(token);

    context.commit('SET_DRUGS', data.items);
  },

  LOAD_DRUG_STATS: async (context) => {
    const token = context.state.token;
    const data = await dashboardApi.getDrugStats(token);

    context.commit('SET_DRUG_STATS', data);
  },

  LOAD_DRUG_PRESCRIPTIONS: async (context, payload) => {
    const token = context.state.token;
    const result = await dashboardApi.getDrugPrescriptions(token, payload);
    const data = { items: result.items, clientId: payload };

    context.commit('SET_DRUG_PRESCRIPTIONS', data);
  },

  ADD_DRUG_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;
    const { data } = payload;

    await dashboardApi.addDrugPrescription(token, data);

    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', data.client);
  },

  UPDATE_DRUG_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;
    const { prescriptionId, clientId, data } = payload;

    await dashboardApi.updateDrugPrescription(token, prescriptionId, data);

    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', clientId);
  },

  ARCHIVE_DRUG_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;
    const { prescriptionId, clientId } = payload;

    await dashboardApi.archiveDrugPrescription(token, prescriptionId);

    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', clientId);
  },

  RESTORE_DRUG_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;
    const { prescriptionId, clientId } = payload;

    await dashboardApi.restoreDrugPrescription(token, prescriptionId);

    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', clientId);
  },

  DELETE_DRUG_PRESCRIPTION: async (context, payload) => {
    const token = context.state.token;
    const { prescriptionId, clientId } = payload;

    await dashboardApi.deleteDrugPrescription(token, prescriptionId);

    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', clientId);
  },

  LOAD_FAVORITES_DRUG_PRESCRIPTIONS: async (context) => {
    const token = context.state.token;

    const data = await dashboardApi.getFavoritesDrugPrescriptions(token);

    context.commit('SET_FAVORITES_DRUG_PRESCRIPTIONS', data.items);
  },

  ADD_DRUG_PRESCRIPTION_TO_FAVORITES: async (context, payload) => {
    const token = context.state.token;
    const { prescriptionId, clientId } = payload;

    await dashboardApi.addDrugPrescriptionToFavorites(token, prescriptionId);

    context.dispatch('LOAD_FAVORITES_DRUG_PRESCRIPTIONS', clientId);
    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', clientId);
  },

  REMOVE_DRUG_PRESCRIPTION_FROM_FAVORITES: async (context, payload) => {
    const token = context.state.token;
    const { prescriptionId, clientId } = payload;

    await dashboardApi.removeDrugPrescriptionFromFavorites(token, prescriptionId);

    context.dispatch('LOAD_FAVORITES_DRUG_PRESCRIPTIONS', clientId);
    context.dispatch('LOAD_DRUG_PRESCRIPTIONS', clientId);
  },

  LOAD_QUESTIONNAIRES: async (context) => {
    const token = context.state.token;
    const response = await questionnaireApi.getQuestionnaires(token);

    context.commit('SET_QUESTIONNAIRES', response.items);
  },

  LOAD_QUESTIONNAIRE_ANSWERS: async (context, payload) => {
    const token = context.state.token;
    const response = await questionnaireApi.getUserAnswers(token, payload);
    const data = { clientId: payload, items: response.items };

    context.commit('SET_QUESTIONNAIRE_ANSWERS', data);
  },

  ADD_QUESTIONNAIRE_COMMENT: async (context, payload) => {
    const token = context.state.token;
    const { id, comment, client } = payload;

    await questionnaireApi.addAnswerComment(token, id, comment);

    await context.dispatch('LOAD_QUESTIONNAIRE_ANSWERS', client);
  },
};

export default new Vuex.Store({
  state,
  getters,
  mutations,
  actions,
  modules: {
    checkup,
    test,
    clinic,
  },
});